/**
 * Data layer for `payout-threshold-enforcement-min-balance-before-re`. Thin typed fetch wrappers over /api/v1/payout-threshold-enforcement-min-balance-before-re.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PayoutThresholdEnforcementMinBalanceBeforeReRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPayoutThresholdEnforcementMinBalanceBeforeReResponse {
	count: number;
	items: PayoutThresholdEnforcementMinBalanceBeforeReRow[];
}

export interface CreatePayoutThresholdEnforcementMinBalanceBeforeReBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/payout-threshold-enforcement-min-balance-before-re";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPayoutThresholdEnforcementMinBalanceBeforeReResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPayoutThresholdEnforcementMinBalanceBeforeReResponse>(res);
}

export async function create(body: CreatePayoutThresholdEnforcementMinBalanceBeforeReBody): Promise<PayoutThresholdEnforcementMinBalanceBeforeReRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PayoutThresholdEnforcementMinBalanceBeforeReRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const payoutThresholdEnforcementMinBalanceBeforeReQueryKeys = {
	all: ["payout-threshold-enforcement-min-balance-before-re"] as const,
	list: () => [...payoutThresholdEnforcementMinBalanceBeforeReQueryKeys.all] as const,
	detail: (id: string) => [...payoutThresholdEnforcementMinBalanceBeforeReQueryKeys.all, id] as const,
};
